import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Chip, IconButton, Stack, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import { type GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { PageHeader } from '../../../../shared/components/PageHeader';
import { DataTable, type DataTableQuery } from '../../../../shared/components/DataTable';
import { GRID_PAGE_SIZE, usePersistedGridState } from '../../../../shared/grid/usePersistedGridState';
import { IncludeInactiveToggle } from '../../../../shared/components/IncludeInactiveToggle';
import { ConfirmDialog } from '../../../../shared/components/ConfirmDialog';
import { ErrorState } from '../../../../shared/components/ErrorState';
import { useToast } from '../../../../shared/stores/toast.store';
import { describeApiError } from '../../../../shared/api/errors';
import { clientsHooks } from '../../hooks';
import { useClientProjectsHooks } from '../api';
import { ClientProjectFormDialog } from '../ClientProjectFormDialog';
import { ProjectRequisicoesDialog } from '../ProjectRequisicoesDialog';
import type { ClientProjectDto, ClientProjectListItem } from '../types';

const DEFAULT_QUERY: DataTableQuery = {
  page: 1,
  pageSize: GRID_PAGE_SIZE,
  sortBy: 'nome',
  sortOrder: 'asc',
};

export function ClientProjectsPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const params = useParams<{ clientId: string }>();
  const clientId = Number(params.clientId);

  const { query, setQuery, clear } = usePersistedGridState('projetos-cliente', DEFAULT_QUERY, {});
  const [editing, setEditing] = useState<ClientProjectDto | null | 'new'>(null);
  const [pendingDelete, setPendingDelete] = useState<ClientProjectListItem | null>(null);
  const [rcsFor, setRcsFor] = useState<ClientProjectListItem | null>(null);

  const clientDetail = clientsHooks.useDetail(Number.isFinite(clientId) ? clientId : null);
  const projects = useClientProjectsHooks(clientId);
  const listQuery = projects.useList(query);
  const removeMutation = projects.useDelete();

  useEffect(() => {
    if (!Number.isFinite(clientId)) navigate('/clientes', { replace: true });
  }, [clientId, navigate]);

  async function openEdit(row: ClientProjectListItem): Promise<void> {
    try {
      setEditing(await projects.api.get(row.id));
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  async function confirmDelete(): Promise<void> {
    if (!pendingDelete) return;
    try {
      await removeMutation.mutateAsync(pendingDelete.id);
      toast.success(t('features.clientes.projetos.toasts.deleted'));
      setPendingDelete(null);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  const columns: GridColDef<ClientProjectListItem>[] = [
    { field: 'sigla', headerName: t('features.clientes.projetos.columns.sigla'), width: 120 },
    {
      field: 'nome',
      headerName: t('features.clientes.projetos.columns.nome'),
      flex: 1,
      minWidth: 240,
    },
    {
      field: 'totalRequisicaoCompra',
      headerName: t('features.clientes.projetos.columns.totalRC'),
      width: 160,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'saldoRequisicaoCompra',
      headerName: t('features.clientes.projetos.columns.saldoRC'),
      width: 160,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'ativo',
      headerName: t('features.clientes.projetos.columns.ativo'),
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={
            params.value
              ? t('features.clientes.projetos.status.active')
              : t('features.clientes.projetos.status.inactive')
          }
          color={params.value ? 'success' : 'default'}
          size="small"
          variant={params.value ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'atualizadoEm',
      headerName: t('features.clientes.projetos.columns.atualizadoEm'),
      width: 170,
      valueFormatter: (value: string | null) =>
        value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '—',
    },
    {
      field: 'actions',
      headerName: '',
      width: 130,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t('features.clientes.projetos.requisicoes.tooltip')}>
            <IconButton
              size="small"
              aria-label={t('features.clientes.projetos.requisicoes.aria', {
                nome: params.row.nome,
              })}
              onClick={() => setRcsFor(params.row)}
            >
              <RequestQuoteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.edit')}>
            <IconButton
              size="small"
              aria-label={t('features.clientes.projetos.actions.edit', { nome: params.row.nome })}
              onClick={() => void openEdit(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.delete')}>
            <IconButton
              size="small"
              color="error"
              aria-label={t('features.clientes.projetos.actions.delete', { nome: params.row.nome })}
              onClick={() => setPendingDelete(params.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  const clientName = clientDetail.data?.nome ?? '';
  const title = clientName
    ? t('features.clientes.projetos.titleFor', { nome: clientName })
    : t('features.clientes.projetos.title');

  if (listQuery.isLoadingError) {
    return (
      <>
        <PageHeader title={title} />
        <ErrorState
          description={describeApiError(listQuery.error)}
          onRetry={() => listQuery.refetch()}
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={title}
        breadcrumbs={[
          { label: t('menu.clients') },
          { label: t('menu.clientsRegistry'), to: '/clientes' },
          { label: t('features.clientes.projetos.breadcrumbLeaf') },
        ]}
        actions={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/clientes')}
            >
              {t('features.clientes.projetos.actions.back')}
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing('new')}>
              {t('features.clientes.projetos.actions.new')}
            </Button>
          </Stack>
        }
      />

      <DataTable<ClientProjectListItem>
        rows={listQuery.data?.data ?? []}
        columns={columns}
        total={listQuery.data?.meta.total ?? 0}
        loading={listQuery.isLoading || listQuery.isFetching}
        query={query}
        onQueryChange={setQuery}
        onClear={clear}
        searchPlaceholder={t('features.clientes.projetos.searchPlaceholder')}
        filters={<IncludeInactiveToggle query={query} onChange={setQuery} />}
      />

      <ClientProjectFormDialog
        open={editing !== null}
        project={editing === 'new' ? null : editing}
        hooks={{ useCreate: projects.useCreate, useUpdate: projects.useUpdate }}
        onClose={() => setEditing(null)}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('features.clientes.projetos.deleteConfirm.title')}
        description={t('features.clientes.projetos.deleteConfirm.description', {
          nome: pendingDelete?.nome ?? '',
        })}
        confirmLabel={t('common.delete')}
        loading={removeMutation.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />

      {rcsFor && (
        <ProjectRequisicoesDialog
          open
          clientId={clientId}
          projectId={rcsFor.id}
          projectNome={rcsFor.nome}
          onClose={() => setRcsFor(null)}
        />
      )}
    </>
  );
}
